import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { financeApi } from '../../services/financeApi.js';
import { inventoryApi } from '../../services/inventoryApi.js';
import { formatDate } from '../../utils/formatters.js';
import { purchaseOrderPdfAttachment } from '../../utils/pdf.js';

/**
 * E-mails the purchase order PDF to the supplier via the configured SMTP
 * server (finance /email/send); the body mentions the requested delivery
 * date when one is set. Callers only offer this from CONFIRMED on -
 * drafts and unapproved POs never reach a supplier. Pass any PO row
 * (`{id, poNumber}`) - detail incl. lines is fetched here.
 */
export function PurchaseOrderSendModal({ po, onClose }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const open = Boolean(po);
  const id = po?.id;

  const detail = useQuery({
    queryKey: ['inventory', 'purchase-orders', id],
    queryFn: () => inventoryApi.getPurchaseOrder(id).then((r) => r.items),
    enabled: open,
  });
  const suppliers = useQuery({
    queryKey: ['inventory', 'suppliers'],
    queryFn: () => inventoryApi.listSuppliers().then((r) => r.items || []),
    enabled: open,
  });
  const settings = useQuery({
    queryKey: ['finance', 'company-settings'],
    queryFn: () => financeApi.getCompanySettings().then((r) => r.items),
    retry: false,
    enabled: open,
  });

  const supplier = (suppliers.data || []).find((s) => s.id === detail.data?.supplierId);

  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');

  useEffect(() => {
    if (!open || !detail.data) return;
    const p = detail.data;
    setTo(supplier?.email || '');
    const base = t('po.email.subject', { number: p.poNumber });
    setSubject(settings.data?.companyName ? `${base} - ${settings.data.companyName}` : base);
    const expected = p.expectedDate
      ? `\n\n${t('po.email.expected', { date: formatDate(p.expectedDate) })}`
      : '';
    setBody(t('po.email.body', { number: p.poNumber }) + expected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, detail.data?.id, supplier?.email, settings.data?.companyName]);

  const sendMail = useMutation({
    mutationFn: async () => {
      const att = await purchaseOrderPdfAttachment({
        po: detail.data,
        supplier,
        settings: settings.data,
        t,
      });
      return financeApi.sendEmail({
        to,
        subject,
        text: body,
        attachmentBase64: att.base64,
        attachmentName: att.name,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory', 'purchase-orders'] });
      onClose();
    },
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('po.email.title')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={sendMail.isPending}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" onClick={() => sendMail.mutate()} disabled={sendMail.isPending || !to || !detail.data}>
            {sendMail.isPending ? `${t('email.send')}…` : t('email.send')}
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
        <p className="muted text-sm" style={{ margin: 0 }}>{t('email.intro')}</p>
        <label className="field">
          <span className="field-label">{t('email.to')}</span>
          <input className="input" type="email" value={to} onChange={(e) => setTo(e.target.value)} required />
        </label>
        <label className="field">
          <span className="field-label">{t('email.subject')}</span>
          <input className="input" value={subject} onChange={(e) => setSubject(e.target.value)} required />
        </label>
        <label className="field">
          <span className="field-label">{t('email.message')}</span>
          <textarea className="textarea" rows={7} value={body} onChange={(e) => setBody(e.target.value)} />
        </label>
        {sendMail.isError && <div className="alert danger">{sendMail.error?.message || t('common.loadError')}</div>}
      </div>
    </Modal>
  );
}
